<template>
  <div class="ct-send-email-component">
    <!--Email Form-->
    <template v-if="emailAccountOptions.length > 0 && toEmailOptions.length > 0">
      <form-form @submit="handleSend()" :submit-error="submitError" :validator="$v">
        <form-select empty="Choose Template..."
                     v-model="emailTemplateId"
                     :options="emailTemplateOptions">
          <form-help-block v-if="emailTemplateOptions && emailTemplateOptions.length === 0">
            &nbsp;<a @click="goToRoute({ name: 'emailTemplates' })">Create a Template</a>
          </form-help-block>

        </form-select>
        <div class="grid-x">
          <div class="cell auto">
            <form-select empty="To Email Address..."
                         v-model="toEmail"
                         :options="toEmailOptions"
                         select-class="fs-block"
                         :validator="$v.toEmail">
            </form-select>
          </div>
          <div class="cell shrink" v-if="!showCcBlock">
            <a class="button clear secondary" @click="editCc = !editCc">Cc</a>
          </div>
        </div>
        <div class="ccBlock" v-if="showCcBlock">
          <!--Edit Cc/Bcc addresses-->
          <template v-if="editCc">
            <div class="grid-x align-middle ccInput">
              <div class="cell auto expand">
                <form-tags-input-element v-model="cc" validate-email placeholder="Add Email"></form-tags-input-element>
              </div>
              <div class="cell shrink" style="width: 2.3em">
                &nbsp;&nbsp;Cc
              </div>
            </div>
            <div class="grid-x align-middle ccInput">
              <div class="cell auto">
                <form-tags-input-element v-model="bcc" validate-email placeholder="Add Email"></form-tags-input-element>
              </div>
              <div class="cell shrink" style="width: 2.3em">
                &nbsp;&nbsp;Bcc
              </div>
            </div>
          </template>
          <!--Show CC/BCC addresses listed-->
          <div class="ccList" v-else>
            <div v-if="cc && cc.length > 0" @click="editCc = true">
              <small>
                <span>Cc:</span> {{ cc.join(', ') }}
              </small>
            </div>
            <div v-if="bcc && bcc.length > 0" @click="editCc = true">
              <small>
                <span>Bcc:</span> {{ bcc.join(', ') }}
              </small>
            </div>
          </div>
        </div>
        <form-input placeholder="Subject" v-model="subject" :validator="$v.subject"></form-input>
        <div class="margin-bottom">
          <quill-editor :merge-tag-values="mergeTagValues" compact ref="quill"></quill-editor>
          <div style="margin-bottom: 0.6rem" v-if="attachments && attachments.length > 0"></div>
          <email-attachment ref="attachmentModal" :attachments="attachments" @data="attachments = $event"></email-attachment>
        </div>
        <div class="grid-x">
          <div class="cell auto">
            <form-select empty="From Email Address..."
                         v-model="emailAccountId"
                         :options="emailAccountOptions"
                         :validator="$v.emailAccountId">
            </form-select>
          </div>
          <div class="shrink">
            <a class="button clear secondary" title="Add Attachment" @click="$refs.attachmentModal.openUppy()">
              <i class="fas fa-paperclip"></i>
            </a>
          </div>
        </div>
        <template v-if="contact.do_not_contact">
          <div class="label alert">Do Not Contact</div>
        </template>
        <template v-else>
          <form-submit :loading="isLoading">Send Email</form-submit>
        </template>
      </form-form>
    </template>

    <!--No Email Account-->
    <div v-else-if="emailAccountOptions.length === 0">
      <p>You can use Call Time to quickly send templated follow-up emails to any of your contacts.</p>
      <p>To get started, you must first setup your email account in your campaign settings.</p>
      <p>
        <a class="button" @click="goToRoute({ name: 'emailAccounts' })">Setup Email Account</a>
      </p>
    </div>

    <!--No Contact Emails-->
    <div v-else-if="toEmailOptions.length === 0">
      <p>You do not have an email address saved for this contact. You can add one by editing the contact.</p>
      <p>
        <a class="button" @click="goToRoute({ name: 'editContact', params: { id: contact.id } })">Edit Contact</a>
      </p>
    </div>

  </div>
</template>

<script>
import { formsMixin } from '../../mixins/forms/formsMixin'
import { emailAccountsQuery, sendEmailMutation } from '../../graphql/email'
import { emailTemplateQuery, emailTemplatesQuery, templateMergeTagsQuery } from '../../graphql/emailTemplates'
import { compileTemplateFromContact } from '../../services/TemplatesService'
import QuillEditor from '../TemplateEditors/QuillEditor'
import { required, email } from 'vuelidate/lib/validators'
import FormTagsInputElement from '../../mixins/forms/components/FormTagsInputElement'
import EmailAttachment from './EmailAttachment'

export default {
  mixins: [
    formsMixin
  ],
  components: {
    FormTagsInputElement,
    QuillEditor,
    EmailAttachment
  },
  props: {
    contact: Object
  },
  data () {
    return {
      // if there is only one email address for the contact, then select it as the default address
      toEmail: this.contact && this.contact.emails && this.contact.emails.length === 1 ? this.contact.emails[0].email : null,
      cc: [],
      bcc: [],
      emailAccountId: null,
      emailTemplateId: null,
      subject: null,
      quillDelta: null,
      bodyHtml: null,
      submitError: null,
      isSending: false,
      editCc: false,
      attachments: []
    }
  },
  apollo: {
    emailAccounts: {
      query: emailAccountsQuery,
      update (data) {
        // if there is only one email account, set it as the default email account id
        const sendFromEmails = data.emailAccounts.filter(email => !!email.is_send_from_enabled)
        if (sendFromEmails.length === 1) {
          this.emailAccountId = sendFromEmails[0].id
        }
        return data.emailAccounts
      }
    },
    emailTemplates: emailTemplatesQuery,
    emailTemplate: {
      query: emailTemplateQuery,
      variables () {
        return {
          id: this.emailTemplateId
        }
      },
      skip () {
        return !this.emailTemplateId
      }
    },
    templateMergeTags: {
      query: templateMergeTagsQuery,
      variables () {
        return {
          contactId: this.contact.id
        }
      }
    }
  },
  validations: {
    toEmail: {
      required,
      email
    },
    cc: {
      $each: { email }
    },
    bcc: {
      $each: { email }
    },
    subject: {
      required
    },
    emailAccountId: {
      required
    }
  },
  computed: {
    showCcBlock () {
      return this.editCc || (this.cc && this.cc.length > 0) || (this.bcc && this.bcc.length > 0)
    },
    toEmailOptions () {
      if (!this.contact.emails) {
        return []
      }
      return this.contact.emails.map(email => {
        return {
          text: email.email,
          value: email.email
        }
      })
    },
    emailAccountOptions () {
      if (this.emailAccounts && this.emailAccounts.length > 0) {
        const options = []
        this.emailAccounts.forEach(emailAccount => {
          if (emailAccount.is_send_from_enabled) {
            options.push({
              text: 'From: ' + emailAccount.email,
              value: emailAccount.id
            })
          }
        })
        return options
      }
      return []
    },
    emailTemplateOptions () {
      if (this.emailTemplates && this.emailTemplates.length > 0) {
        return this.emailTemplates.map(emailTemplate => {
          return {
            text: emailTemplate.name,
            value: emailTemplate.id
          }
        })
      }
      return []
    },
    mergeTagValues () {
      if (this.templateMergeTags && this.templateMergeTags.length > 0) {
        const values = {}
        this.templateMergeTags.forEach(tag => {
          values[tag.key] = tag.value
        })
        return values
      }
      return null
    },
    isLoading () {
      return this.$apollo.loading || this.isSending
    }
  },
  methods: {
    async handleSend () {
      this.submitError = null
      this.isSending = true
      // get the html from Quill
      const html = this.$refs.quill.getHtml()
      if (!html) {
        return
      }
      // format the attachment objects
      const attachments = this.attachments.map(attachment => {
        return {
          upload_id: attachment.upload_id,
          filename: attachment.filename,
          file_type: attachment.file_type
        }
      })
      // send the email
      try {
        await this.$apollo.mutate({
          mutation: sendEmailMutation,
          variables: {
            emailAccountId: this.emailAccountId,
            to: this.toEmail,
            cc: this.cc,
            bcc: this.bcc,
            subject: this.subject,
            html: html,
            attachments: attachments,
            emailTemplateId: this.emailTemplateId
          }
        })
      } catch (e) {
        this.isSending = false
        console.error(e)
        this.submitError = new Error('Failed to send email. ' + e.message)
        return
      }
      // close the form and show success
      this.$notifications.feedbackNotification('Message sent', 'success')
      this.emailTemplateId = null
      this.$emit('close')
    },
    applySelectedEmailTemplate () {
      // if there is no email template, clear the editor
      if (!this.emailTemplate || !this.emailTemplate.id) {
        return this.updateQuillContents(null)
      }
      // otherwise, update the editor content
      const delta = this.emailTemplate.data ? JSON.parse(this.emailTemplate.data) : this.emailTemplate.data
      this.updateQuillContents(delta)
      // set the subject
      this.subject = compileTemplateFromContact(this.emailTemplate.subject || '', this.contact)
      // if there is an email addresses specified, apply that email address
      if (this.emailTemplate.email_account_id) {
        this.emailAccountId = this.emailTemplate.email_account_id
      }
      // set other stuff
      this.cc = this.emailTemplate.cc || []
      this.bcc = this.emailTemplate.bcc || []
      // set attachments
      // use a shallow copy so it's mutable by the attachments component
      this.attachments = this.emailTemplate.attachments ? Array.from(this.emailTemplate.attachments) : []
    },
    updateQuillContents (newDelta) {
      try {
        // use the Quill method to update the contents, so the merge tags will be applied
        this.$refs.quill.setContents(newDelta)
      } catch (err) {
        console.error('Failed to update Quill contents')
      }
    },
    goToRoute (route) {
      // navigate
      this.$router.push(route)
      // emit the close event to close the actions bar
      this.$emit('close')
    }
  },
  watch: {
    'emailTemplate.id': {
      handler (newValue) {
        // whenever the email template updates, apply it to the form
        this.applySelectedEmailTemplate()
      }
    }
  }
}

</script>

<style lang="scss">
.ct-send-email-component {
  .ql-container {
    height: 200px;
  }
}
.ccBlock {
  .ccInput {
    margin-bottom: 1rem;
  }
  .ccList {
    cursor: pointer;
    margin-top: -0.5rem;
    padding-left: 0.1rem;
    margin-bottom: 1rem;
  }
}
</style>
